import { useRouter } from '@tanstack/react-router';
import type { AnchorHTMLAttributes, MouseEvent } from 'react';

/**
 * An in-app link to a path known only at runtime (backend navigation, a guard's target). A real
 * `<a href>`, so it can be opened in a new tab; a plain left click navigates through the router.
 * TanStack's typed `<Link to>` cannot take these paths, and a package cannot know an app's route
 * tree anyway.
 *
 * `href` is an app path (`/leads`); the rendered `href` carries the router's basepath, so a new
 * tab opens the same page when the app is served under a path.
 */
export function AppLink({
  href,
  onClick,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const router = useRouter();
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    void router.navigate({ href });
  };
  const publicHref = href.startsWith('/') ? `${router.basepath.replace(/\/+$/, '')}${href}` : href;
  return <a href={publicHref} onClick={handleClick} {...rest} />;
}
